import { toast } from 'react-toastify'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  PencilIcon,
  RefreshIcon,
  TrashIcon,
  UploadIcon,
  XIcon
} from '@heroicons/react/outline'
import { CheckCircleIcon, BanIcon } from '@heroicons/react/solid'

import { LoadPage } from 'components/LoadPage'
import { StepPanel } from '../components/StepPanel'
import { NotFoundPage } from 'components/NotFoundPage'
import { ReviewTable } from '../components/ReviewTable'
import { ProtectedSection } from 'components/ProtectedSection'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import { CancelButton, SimpleButton } from 'components/FormInputs/Button'
import {
  InputWithValidationError,
  SimpleCustomSelect
} from 'components/FormInputs'

import config from 'config'
import { Errors } from 'types'
import { date, isEmpty, pick } from 'utils'
import { getCurrentClientStatus } from 'modules/Clients/helpers/utils'
import { translations } from '../translations'
import { ReadResult } from 'interfaces/queryOptions'
import {
  buildStatusDatesForStepsPanel,
  includeStatus,
  updateSteps
} from '../helpers/utils'
import { HardwareDetails, HardwareCart } from '../types'
import { list as listDevices } from 'services/devices'
import { getDeviceTypesById } from 'modules/Items/Devices/services/api'
import { showFreeBillingInventory } from 'services/billing/inventories'
import { DatePickerWithError } from 'components/FormInputs/DatePicker'
import {
  nonLogisticsOrderBaseSteps,
  orderBaseSteps,
  writeOffMotives
} from '../helpers/constants'
import {
  Client,
  ClientStatus,
  Currency,
  Device,
  Installment,
  Nature,
  Order,
  OrderHardwareStatus,
  OrderStatus,
  Plan,
  Role,
  BillingInventory
} from 'models'
import {
  addItems,
  assignEnterprise,
  cancel,
  deliver,
  listItems,
  listEnterprises,
  removeItems,
  show,
  updateItemFiscalData,
  updateItemStatus,
  updateItemWriteOff,
  updateInstallmentDates,
  updateOrderDates,
  updateStatus
} from '../services'
import { FiscalData, WriteOffInvoiceMotives } from 'models/orderItem'
import { Label } from 'components/Label'
import { isSameDay, setTodayTimeFrom } from 'utils/datetime'
import {
  determineCancelationMinDate,
  getOrderStatusAndDate,
  orderStatusSequence,
  orderStatusToDateColumn
} from 'utils/order-status'
import { AuthContext } from 'modules/Auth'
import { CheckBox } from 'modules/Clients/components/Tabs/View/Enterprise/components/CheckBox'
import { InstallmentTable } from '../components/Installments'
import { show as showClient } from 'modules/Clients/services/read'
import { Cell, Pie, PieChart } from 'recharts'
import { HardwareDynamicModal } from '../components/Modal'
import { AssignOwnerModal } from '../components/AssignOwnerModal'
import { MigrateOrderItemsModal } from '../components/Modal/MigrateOrderItems'
import { Modal } from 'components/Modal'
import { ResizibleTextArea } from 'components/FormInputs/TextArea/ResizibleTextArea'

const writeOffOptions: Record<string, any> = {
  default: [{ value: '', label: 'Escolha um estado' }],
  canceled: [{ value: 'loss', label: 'Perda' }],
  returned: [{ value: 'return', label: 'Retorno' }],
  ownership_changed: [{ value: 'asset_sale', label: 'Venda de ativo' }]
}

const SOFTRUCK_BBOX = {
  minLat: -19.94099242618927,
  maxLat: -19.936640442928834,
  minLng: -43.93996686738501,
  maxLng: -43.93771381716789
}

const isInSoftruckBBOX = (lat: number, lng: number) => {
  return (
    lat >= SOFTRUCK_BBOX.minLat &&
    lat <= SOFTRUCK_BBOX.maxLat &&
    lng >= SOFTRUCK_BBOX.minLng &&
    lng <= SOFTRUCK_BBOX.maxLng
  )
}

const shouldFetchDevices = (currentStatus: string) => {
  return [
    'in_progress',
    'updating_fiscal_data',
    'ready_for_pick_up',
    'shipping',
    'delivered',
    'canceled'
  ].includes(currentStatus)
}

const makeUnknownDeviceDetails = (): HardwareDetails => {
  return {
    valid: false,
    saved: false,
    id: undefined,
    hint: () => (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
        Validando
      </span>
    ),
    attributes: {}
  }
}

const makeValidDeviceDetails = (
  data: ReadResult<BillingInventory>
): HardwareDetails => {
  const meta = data.attributes.meta ?? {}

  return {
    valid: true,
    saved: false,
    // id do item de billing_inventories — é o que é enviado para POST /orders/{id}/items, não o id do device
    id: data.id,
    hint: () => (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800">
        Válido
      </span>
    ),
    attributes: {
      id: data.id,
      imei: data.attributes.identifier,
      name: meta.name,
      nature: meta.nature,
      fiscal_data: undefined
    }
  }
}

const makeInvalidDeviceDetails = (): HardwareDetails => {
  return {
    valid: false,
    saved: false,
    id: undefined,
    hint: () => (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
        Inválido
      </span>
    ),
    attributes: {}
  }
}

const makeSavedDeviceDetails = (
  data: Device & {
    fiscalData?: FiscalData
    itemStatus: OrderHardwareStatus
    itemStatusCreatedAt: Date
  }
): HardwareDetails => {
  return {
    valid: true,
    saved: true,
    id: data.id,
    hint: () => (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
        Salvo
      </span>
    ),
    attributes: data,
    status: data.itemStatus,
    status_updated_at: data.itemStatusCreatedAt
  }
}

export function ShowHardwareOrder() {
  const { orderId } = useParams() as unknown as { orderId: number }

  const navigate = useNavigate()

  const { user } = useContext(AuthContext)
  const userRole = user?.relationships?.role.attributes.name as Role

  const [isLoading, setIsLoading] = useState(true)
  const [disabled, setDisabled] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [steps, setSteps] = useState(
    orderBaseSteps.map((step) => Object.assign({}, step))
  )

  const [order, setOrder] = useState<Order>()
  const [isAssignOwnerOpen, setIsAssignOwnerOpen] = useState(false)

  const [currentOrderStatus, setCurrentOrderStatus] = useState(
    OrderStatus.pending
  )

  const [statusDates, setStatusDates] = useState<Record<string, any>>({})

  const [canceledAt, setCanceledAt] = useState(new Date())
  const canceledAtRef = useRef(canceledAt)

  const [newOrderStatusUpdatedAt, setNewOrderStatusUpdatedAt] = useState(
    new Date()
  )
  const newOrderStatusUpdatedAtRef = useRef(newOrderStatusUpdatedAt)

  const [newOrderStatus, setNewOrderStatus] = useState<OrderStatus>()
  const newOrderStatusRef = useRef(newOrderStatus)

  const orderDateEditRef = useRef(new Date())
  const installmentDateEditRef = useRef(new Date())

  const [itemsSelected, setItemsSelected] = useState<number[]>([])

  const handleCheckBoxChange = (id: number) => {
    setItemsSelected((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    )
  }

  const handleAllCheckBoxChange = () => {
    const allIds = Object.values(cartDetails)
      .filter((item) => item.status === 'ordered')
      .map((item) => item.id as number)

    setItemsSelected((prev) =>
      allIds.every((id) => prev.includes(id)) ? [] : allIds
    )
  }

  useEffect(() => {
    canceledAtRef.current = canceledAt
    newOrderStatusUpdatedAtRef.current = newOrderStatusUpdatedAt
    newOrderStatusRef.current = newOrderStatus
  }, [canceledAt, newOrderStatusUpdatedAt, newOrderStatus])

  const [itemToBeUpdatedAt, setItemToBeUpdatedAt] = useState(new Date())
  const itemToBeUpdatedAtRef = useRef(itemToBeUpdatedAt)

  useEffect(() => {
    itemToBeUpdatedAtRef.current = itemToBeUpdatedAt
  }, [itemToBeUpdatedAt])

  const [plan, setPlan] = useState<Partial<Plan>>()
  const [product, setProduct] = useState<any>(null)
  const [client, setClient] = useState<Partial<Client>>()
  const [currentImei, setCurrentImei] = useState<string>()

  const [clientStatus, setClientStatus] = useState<ClientStatus>()

  const [cart, setCart] = useState<Set<string>>(new Set())
  const [cartDetails, setCartDetails] = useState<HardwareCart>({})

  const [itemsNumberByStatus, setItemsNumberByStatus] =
    useState<Record<OrderHardwareStatus, number>>()

  useEffect(() => {
    const statusCount: Record<OrderHardwareStatus, number> = {
      ordered: 0,
      canceled: 0,
      returned: 0,
      ownership_changed: 0,
      migrated: 0
    }

    Object.values(cartDetails).forEach((item) => {
      const status = item.status as OrderHardwareStatus
      if (status in statusCount) {
        statusCount[status]++
      }
    })

    setItemsNumberByStatus(statusCount)
  }, [cartDetails])

  const [devicesUnderValidation, setDevicesUnderValidation] = useState<
    Set<string>
  >(new Set())
  const [validationQueue, setValidationQueue] = useState<string[]>([])
  const validationQueueRef = useRef<string[]>([])
  const [showConfirmationDialog] = useConfirmationDialog()

  const [selectedDevice, setSelectedDevice] = useState<string>()

  const [currentOrderHardwareStatus, setCurrentOrderItemStatus] =
    useState<OrderHardwareStatus>('ordered')

  const [currentModal, setCurrentModal] = useState<
    null | 'cancel_fiscal_data' | 'update_item_status'
  >()

  const [isMigrateModalOpen, setIsMigrateModalOpen] = useState(false)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [isWriteOffModalOpen, setIsWriteOffModalOpen] = useState(false)
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false)
  const [enterprises, setEnterprises] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<
    number | undefined
  >()
  const [writeOffCorrection, setWriteOffCorrection] = useState<{
    write_off_date: Date
    write_off_invoice: string | undefined
  }>({ write_off_date: new Date(), write_off_invoice: undefined })
  const [bulkInput, setBulkInput] = useState('')
  const csvInputRef = useRef<HTMLInputElement>(null)

  const [fiscalData, setFiscalData] = useState({
    write_off_date: new Date(),
    write_off_invoice: undefined,
    write_off_invoice_motive: undefined
  } as Partial<FiscalData>)

  const [installments, setInstallments] = useState<Installment[]>([])

  const [errors, setErrors] = useState<Errors>()

  useEffect(() => {
    setNewOrderStatusUpdatedAt(
      setTodayTimeFrom(
        new Date(
          getOrderStatusAndDate({
            id: order?.id as number,
            type: 'orders',
            attributes: { ...order }
          }).date!
        )
      )
    )
  }, [order])

  const updateOrderStatus = useCallback(
    (currentStatus: OrderStatus) => {
      setCurrentOrderStatus(currentStatus)
      setSteps([...updateSteps(steps, currentStatus)])

      return currentStatus
    },
    [steps]
  )

  const setFocus = useCallback(() => {
    const element = document.getElementById('imei')
    element?.focus()
  }, [])

  useEffect(() => {
    if (!orderId) return

    const fetchOrderDevices = async (data: Record<string, any>[]) => {
      const details: HardwareCart = {}

      const orderItemDataMap = data.reduce((map, d) => {
        map[d.attributes.item_id] = d.attributes
        return map
      }, {} as Record<string, any>)

      try {
        const items = await listItems(orderId)

        for (const item of items.data) {
          const imei = item.attributes.imei as string
          const itemData = orderItemDataMap[item.id] ?? {}

          details[imei] = makeSavedDeviceDetails({
            ...item.attributes,
            id: item.id,
            relationships: item.relationships,
            fiscal_data: pick(itemData, [
              'write_off_invoice',
              'write_off_date',
              'write_off_invoice_motive'
            ]),
            itemStatus: itemData.status,
            itemStatusCreatedAt:
              item.relationships.order_devices[0].attributes.created_at
          } as any)

          cart.add(imei)
        }

        setCartDetails({ ...cartDetails, ...details })
        setCart(new Set(cart))
      } catch (err) {
        toast.error('Falha ao buscar os rastreadores do pedido!')
      }
    }

    const fetchOrder = async () => {
      try {
        const order = await show(orderId, {
          includes: {
            client: ['id'],
            plan: [
              'id',
              'name',
              'fiscal_data',
              'meta',
              'nature',
              'logistics',
              'currency',
              'amount',
              'recurring',
              'interval'
            ],
            product: ['*'],
            order_devices: [
              'item_id',
              'status',
              'write_off_date',
              'write_off_invoice',
              'write_off_invoice_motive'
            ],
            coupon: ['*'],
            order_installments: ['*'],
            staff_member: ['id', 'fullname']
          }
        })

        if (order.relationships?.order_installments) {
          setInstallments(
            order.relationships.order_installments
              .map(({ id, attributes }: any) => ({
                id,
                type: attributes.type,
                amount: attributes.amount,
                due_at: attributes.due_at,
                upfront: attributes.upfront,
                eligible: attributes.eligible
              }))
              .sort(
                (a: Installment, b: Installment) =>
                  new Date(a.due_at).getTime() - new Date(b.due_at).getTime()
              )
          )
        }

        setOrder(order as Order)

        setPlan({
          id: order?.relationships?.plan?.id,
          ...(order?.relationships?.plan?.attributes ?? {})
        })

        setProduct(order?.relationships?.product?.attributes ?? null)

        const client = await showClient(order?.relationships?.client?.id, {
          includes: { statuses: ['*'] }
        })

        setClient({ id: client.id, ...client.attributes })
        setClientStatus(
          getCurrentClientStatus(client.relationships?.statuses)?.attributes
            .status
        )

        const currentStatus = updateOrderStatus(
          order.current_status as OrderStatus
        )

        setStatusDates(buildStatusDatesForStepsPanel(order))

        if (!order.relationships?.plan.attributes.logistics) {
          setSteps(updateSteps(nonLogisticsOrderBaseSteps, currentStatus))
        }

        if (['ready_for_pick_up', 'shipping'].includes(currentStatus)) {
          try {
            const subs = await listEnterprises(client.id)
            setEnterprises(subs)
          } catch {
            // enterprise list is non-critical; fail silently
          }
        }

        if (shouldFetchDevices(currentStatus)) {
          await Promise.all([
            fetchOrderDevices(order?.relationships?.order_devices ?? [])
          ])
        }

        setIsLoading(false)
      } catch (err: any) {
        navigate('/orders')
        toast.error(
          err.suggestedMessage ?? 'Falha ao buscar os dados do pedido'
        )
      }
    }
    fetchOrder()
  }, [orderId, isMigrateModalOpen, refreshTrigger])

  const onCancelConfirmation = useCallback(async () => {
    try {
      const cancelDate = canceledAtRef.current
      if (isSameDay(new Date(), cancelDate)) {
        await cancel(orderId)
      } else {
        await cancel(orderId, undefined, cancelDate)
      }
      const data = await show(orderId, {
        attributes: ['id', 'current_status']
      })
      updateOrderStatus(data.current_status as OrderStatus)
      toast.success('Pedido cancelado com sucesso!')
    } catch (err: any) {
      toast.error(err.suggestedMessage ?? 'Não foi possível cancelar o pedido!')
    }
  }, [orderId, updateOrderStatus])

  const onCancelRequest = useCallback(() => {
    if (
      [
        OrderStatus.ready_for_pick_up,
        OrderStatus.shipping,
        OrderStatus.delivered
      ].includes(currentOrderStatus) &&
      plan?.fiscal_data
    ) {
      setFiscalData({
        write_off_date: undefined,
        write_off_invoice: undefined,
        write_off_invoice_motive: undefined
      })
      setCurrentModal('cancel_fiscal_data')
    } else {
      showAndUpdateConfirmationDialog(
        cancelOrderModalAttributes,
        canceledAt,
        true
      )
    }
  }, [
    currentOrderStatus,
    onCancelConfirmation,
    plan?.fiscal_data,
    showConfirmationDialog
  ])

  const onStatusChangeConfirmation = useCallback(async () => {
    const newStatus = newOrderStatusRef.current
    const newOrderStatusUpdatedAt = newOrderStatusUpdatedAtRef.current
    try {
      newStatus === OrderStatus.delivered
        ? await deliver(orderId, newOrderStatusUpdatedAt)
        : await updateStatus(
            orderId,
            newStatus as OrderStatus,
            newOrderStatusUpdatedAt
          )

      const data = await show(orderId, {
        includes: {
          client: ['id', 'account_name'],
          plan: [
            'id',
            'name',
            'fiscal_data',
            'meta',
            'nature',
            'logistics',
            'currency',
            'amount'
          ],
          order_devices: [
            'item_id',
            'status',
            'write_off_date',
            'write_off_invoice',
            'write_off_invoice_motive'
          ],
          coupon: ['*']
        }
      })

      setOrder(data as Order)

      setStatusDates(buildStatusDatesForStepsPanel(data))

      updateOrderStatus(data?.current_status as OrderStatus)
      toast.success('Estado atualizado com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível atualizar o estado do pedido!'
      )
    }
  }, [orderId, updateOrderStatus])

  const onStatusChangeRequest = useCallback(
    (status: OrderStatus | '') => {
      if (status === '') return

      setNewOrderStatus(status)

      showAndUpdateConfirmationDialog(
        getUpdateOrderStatusModalAttributes(status),
        newOrderStatusUpdatedAt,
        status === 'pending' ? false : true,
        'status_update'
      )
    },
    [
      onStatusChangeConfirmation,
      showConfirmationDialog,
      newOrderStatusUpdatedAt
    ]
  )

  const onCanceledAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setCanceledAt(d)
    return d
  }, [])

  const onOrderStatusUpdatedAtChange = useCallback((value: any) => {
    const d = value instanceof Date ? value : new Date(value)
    setNewOrderStatusUpdatedAt(d)
    return d
  }, [])

  function showAndUpdateConfirmationDialog(
    attributes: Record<string, any>,
    date: Date | string,
    datePicker: boolean,
    operationType?: string
  ) {
    const orderedAt = new Date(order?.ordered_at as string)
    const deliveryDate = order?.activated_at
      ? new Date(order.activated_at)
      : undefined
    const now = new Date()

    let minDate
    const maxDate = now

    if (operationType && operationType === 'status_update') {
      minDate = new Date(
        getOrderStatusAndDate({
          id: order?.id as number,
          type: 'orders',
          attributes: { ...order }
        }).date as Date
      )
    } else {
      minDate = determineCancelationMinDate(deliveryDate, orderedAt as Date)
    }

    showConfirmationDialog({
      title: attributes.title,
      message: attributes.message,
      onConfirm: attributes.onConfirm,
      datePicker,
      datePickerAttributes: {
        minDate: minDate as Date,
        maxDate: maxDate as Date
      },
      date: new Date(date),
      onDateChange: attributes.onDateChange
    })
  }

  const onOrderDateEditRequest = useCallback(
    (stepLabel: string, currentDate: Date) => {
      const idx = orderStatusSequence.indexOf(stepLabel)
      // Primeiro status da sequência (pending) não tem status anterior — sem limite inferior
      let minDate: Date | undefined =
        idx === 0 ? undefined : new Date(order?.ordered_at as string)
      let maxDate: Date = new Date()

      if (idx !== -1) {
        for (let i = idx - 1; i >= 0; i--) {
          const d = statusDates[orderStatusSequence[i]]
          if (d) {
            minDate = new Date(d)
            break
          }
        }
        for (let i = idx + 1; i < orderStatusSequence.length; i++) {
          const d = statusDates[orderStatusSequence[i]]
          if (d) {
            maxDate = new Date(d)
            break
          }
        }
      }

      orderDateEditRef.current = currentDate
      showConfirmationDialog({
        title: 'Editar data',
        message: 'Alterar a data deste status do pedido?',
        datePicker: true,
        datePickerAttributes: { minDate: minDate as Date, maxDate },
        date: currentDate,
        onDateChange: (val) => {
          const d = val instanceof Date ? val : new Date(val)
          orderDateEditRef.current = d
          return d
        },
        onConfirm: async () => {
          try {
            await updateOrderDates(order?.id as number, {
              [orderStatusToDateColumn[stepLabel]]:
                orderDateEditRef.current.toISOString()
            })
            setRefreshTrigger((t) => t + 1)
            toast.success('Data atualizada com sucesso!')
          } catch (err: any) {
            toast.error(
              err.suggestedMessage ?? 'Não foi possível atualizar a data'
            )
          }
        }
      })
    },
    [order, statusDates, showConfirmationDialog]
  )

  const onInstallmentDateEditRequest = useCallback(
    (installmentId: string, currentDate: Date, index: number) => {
      const installment = installments[index]
      const billingDay = client?.billing_cycle_day ?? 1

      const snapToBillingDay = (d: Date): Date => {
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
        const snapped = new Date(d)
        snapped.setDate(Math.min(billingDay, lastDay))
        return snapped
      }

      const minDate = new Date(order?.ordered_at as string)
      // No meaningful upper bound for installments — they can be future-dated.
      const maxDate = new Date(minDate.getFullYear() + 10, 11, 31)

      // Disable: days already occupied by other installments (same month/year),
      // for non-upfront installments any day other than billing_cycle_day,
      // and dates that would break chronological order with adjacent installments.
      const filterDate = (date: Date): boolean => {
        if (!installment.upfront && date.getDate() !== billingDay) return false

        const hasSameMonthYear = installments.some((inst, i) => {
          if (i === index || !inst?.due_at) return false
          const other = new Date(inst.due_at)
          return (
            date.getMonth() === other.getMonth() &&
            date.getFullYear() === other.getFullYear()
          )
        })
        if (hasSameMonthYear) return false

        const toDay = (d: Date) =>
          new Date(d.getFullYear(), d.getMonth(), d.getDate())
        const prevDates = installments
          .slice(0, index)
          .filter((i) => i?.due_at)
          .map((i) => toDay(new Date(i.due_at)))
        const nextDates = installments
          .slice(index + 1)
          .filter((i) => i?.due_at)
          .map((i) => toDay(new Date(i.due_at)))
        const latestPrev =
          prevDates.length > 0
            ? new Date(Math.max(...prevDates.map((d) => d.getTime())))
            : null
        const earliestNext =
          nextDates.length > 0
            ? new Date(Math.min(...nextDates.map((d) => d.getTime())))
            : null

        if (latestPrev && toDay(date) <= latestPrev) return false
        if (earliestNext && toDay(date) >= earliestNext) return false

        return true
      }

      const initialDate = installment.upfront
        ? currentDate
        : snapToBillingDay(currentDate)
      installmentDateEditRef.current = initialDate

      showConfirmationDialog({
        title: 'Editar vencimento',
        message: 'Alterar a data de vencimento desta parcela?',
        datePicker: true,
        datePickerAttributes: { minDate, maxDate, filterDate },
        date: initialDate,
        onDateChange: (val) => {
          const next = val instanceof Date ? val : new Date(val)
          const snapped = installment.upfront ? next : snapToBillingDay(next)
          installmentDateEditRef.current = snapped
          return snapped
        },
        onConfirm: async () => {
          try {
            await updateInstallmentDates([
              {
                id: installmentId,
                due_at: installmentDateEditRef.current.toISOString()
              }
            ])
            setRefreshTrigger((t) => t + 1)
            toast.success('Vencimento atualizado com sucesso!')
          } catch (err: any) {
            toast.error(
              err.suggestedMessage ?? 'Não foi possível atualizar o vencimento'
            )
          }
        }
      })
    },
    [order, installments, client, showConfirmationDialog]
  )

  const validateDevice = useCallback(
    async (imei: string) => {
      let invalid = false

      if (devicesUnderValidation.has(imei) || validationQueue.includes(imei)) {
        return
      } else if (
        devicesUnderValidation.size < config.max_concurrent_validations
      ) {
        devicesUnderValidation.add(imei)
        setDevicesUnderValidation(new Set(devicesUnderValidation))
        setCartDetails({ ...cartDetails, [imei]: makeUnknownDeviceDetails() })
      } else {
        setValidationQueue((prev) => {
          const next = [...prev, imei]
          validationQueueRef.current = next
          return next
        })
        setCartDetails({ ...cartDetails, [imei]: makeUnknownDeviceDetails() })
        return
      }

      try {
        const billingInventoryItem = await showFreeBillingInventory({
          attributes: ['id', 'identifier', 'meta'],
          filters: [
            { key: 'identifier', op: 'eq', value: imei },
            { key: 'type', op: 'eq', value: 'device' },
            { key: 'status', op: 'eq', value: 'available' }
          ]
        })

        // The user has removed this device from the cart while it was still under validation
        if (!devicesUnderValidation.has(imei)) return

        const allowedSubtypes: string[] | null =
          product?.meta?.allowed_subtypes ?? null

        if (
          allowedSubtypes &&
          billingInventoryItem?.attributes.meta?.device_type_id != null
        ) {
          const deviceTypeId =
            billingInventoryItem.attributes.meta.device_type_id
          // device_type_id ja vem decodificado (id numerico real) - nao da pra
          // filtrar de volta por id via querystring, entao busca o catalogo
          // inteiro e casa localmente
          const deviceTypesById = await getDeviceTypesById()
          const deviceTypeName = deviceTypesById[deviceTypeId]?.name

          if (!deviceTypeName || !allowedSubtypes.includes(deviceTypeName)) {
            invalid = true
          }
        }

        if (
          billingInventoryItem?.attributes.meta?.nature?.toLowerCase() !==
          plan?.nature?.toLowerCase()
        ) {
          invalid = true
        }

        invalid
          ? (cartDetails[imei] = makeInvalidDeviceDetails())
          : (cartDetails[imei] = makeValidDeviceDetails(billingInventoryItem!))
      } catch (err: any) {
        setDisabled(false)
        cartDetails[imei] = makeInvalidDeviceDetails()

        if (err.statusCode !== 404) {
          toast.error(`Um erro desconhecido impediu a validação de: ${imei}!`)
          return
        }
      }

      devicesUnderValidation.delete(imei)

      if (validationQueueRef.current.length) {
        const nextImei = validationQueueRef.current.shift() as string
        setValidationQueue([...validationQueueRef.current])
        validateDevice(nextImei)
      }

      setDisabled(false)
      setFocus()
      setCartDetails({ ...cartDetails })
      setDevicesUnderValidation(new Set(devicesUnderValidation))
    },
    [
      cartDetails,
      devicesUnderValidation,
      plan?.nature,
      product?.meta?.allowed_subtypes,
      setFocus,
      validationQueue
    ]
  )

  const onAddToCart = useCallback(
    async (imei?: string) => {
      if (!imei || imei === '') return

      if (cart.has(imei)) {
        setErrors({ imei: 'O rastreador já está no pedido!' })
        return
      }

      if (cart.size >= (order?.quantity ?? 0)) {
        toast.error('O pedido já está cheio!')
        return
      }

      cart.add(imei)
      setCart(new Set(cart))
      setDisabled(true)

      await validateDevice(imei)
      setCurrentImei('')
      setErrors({ imei: null })
    },
    [cart, order?.quantity, validateDevice]
  )

  const onCsvImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const codes = text
        .split(/[\n,;\r]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join('\n')
      setBulkInput((prev) => (prev ? `${prev}\n${codes}` : codes))
    }
    reader.readAsText(file)
    e.target.value = ''
  }, [])

  const onBulkAddToCart = useCallback(() => {
    const codes = bulkInput
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)

    const capacity = (order?.quantity ?? 0) - cart.size
    let added = 0
    let skipped = 0
    const newImeis: string[] = []

    for (const imei of codes) {
      if (cart.has(imei) || added >= capacity) {
        skipped++
        continue
      }
      cart.add(imei)
      newImeis.push(imei)
      added++
    }

    setCart(new Set(cart))

    if (skipped > 0) {
      toast.warn(`${skipped} item(s) ignorado(s): duplicado ou pedido cheio.`)
    }

    for (const imei of newImeis) {
      cartDetails[imei] = makeUnknownDeviceDetails()
    }
    setCartDetails({ ...cartDetails })

    for (const imei of [...newImeis].reverse()) {
      validateDevice(imei)
    }

    setBulkInput('')
    setIsBulkModalOpen(false)
  }, [bulkInput, cart, cartDetails, order?.quantity, validateDevice])

  const onRemoveFrontCart = useCallback(
    (imei: string) => {
      cart.delete(imei)
      setCart(new Set(cart))

      setErrors({ imei: null })

      const validationIndex = validationQueue.indexOf(imei)

      if (validationIndex >= 0) {
        validationQueue.splice(validationIndex, 1)
        setValidationQueue([...validationQueue])
      }

      if (devicesUnderValidation.has(imei)) {
        devicesUnderValidation.delete(imei)
        setDevicesUnderValidation(new Set(devicesUnderValidation))
      }

      if (cartDetails[imei]) {
        delete cartDetails[imei]
        setCartDetails({ ...cartDetails })
      }
    },
    [cart, cartDetails, devicesUnderValidation, validationQueue]
  )

  const onRemoveFromOrderConfirmation = useCallback(
    async (imei: string) => {
      if (!cartDetails[imei] || !cartDetails[imei].id) {
        toast.error('Não foi possível remover o rastreador do pedido!')
        return
      }

      try {
        await removeItems(orderId, [cartDetails[imei].id as number])
        toast.success('Rastreador removido com sucesso!')

        cart.delete(imei)
        setCart(new Set(cart))

        delete cartDetails[imei]
        setCartDetails({ ...cartDetails })
      } catch (err: any) {
        toast.error(
          err.suggestedMessage ??
            'Não foi possível remover o rastreador do pedido!'
        )
      }
    },
    [cart, cartDetails, orderId]
  )

  const onRemoveFromOrderRequest = useCallback(
    (imei: string) => {
      showConfirmationDialog({
        title: 'Remover item do pedido',
        message: `Tem certeza que deseja remover o rastreador '${imei}' do pedido?`,
        onConfirm: () => onRemoveFromOrderConfirmation(imei)
      })
    },
    [onRemoveFromOrderConfirmation, showConfirmationDialog]
  )

  const onRefreshComunication = useCallback(async () => {
    const deviceIds = []
    for (const item in cartDetails) {
      deviceIds.push(cartDetails[item].id)
    }

    const result = await listDevices({
      attributes: ['id', 'name', 'imei'],
      includes: {
        chips: ['serial', 'number'],
        device_types: ['alias', 'brand_alias'],
        device_last_gps: ['last_connection_time', 'last_gps_time'] // TODO: Add 'last_gps_time->iccid' when available
      },
      filters: [{ key: 'id', op: 'in', value: deviceIds.join(',') }]
    })

    for (const device of result.data) {
      if (!device.attributes.imei) continue
      cartDetails[device.attributes.imei].attributes.relationships =
        device.relationships
    }

    setCartDetails({ ...cartDetails })
  }, [cartDetails])

  const onSaveFiscalData = useCallback(async () => {
    try {
      const payload = {
        attributes: {
          write_off_date: fiscalData.write_off_date,
          write_off_invoice: fiscalData.write_off_invoice,
          write_off_invoice_motive: fiscalData.write_off_invoice_motive
        }
      }

      await updateItemFiscalData(orderId, payload)

      // Atualizar tabela
      for (const index in cartDetails) {
        cartDetails[index].attributes.fiscal_data = {
          write_off_date: fiscalData.write_off_date,
          write_off_invoice: fiscalData.write_off_invoice,
          write_off_invoice_motive: fiscalData.write_off_invoice_motive
        }
      }

      setCartDetails({ ...cartDetails })

      toast.success('Dados fiscais atualizados com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível atualizar os dados fiscais!'
      )
      return
    }
  }, [
    cartDetails,
    fiscalData.write_off_date,
    fiscalData.write_off_invoice,
    fiscalData.write_off_invoice_motive,
    orderId
  ])

  const onSaveWriteOffCorrection = useCallback(async () => {
    try {
      await updateItemWriteOff(orderId, {
        write_off_date: writeOffCorrection.write_off_date,
        write_off_invoice: writeOffCorrection.write_off_invoice!
      })
      for (const index in cartDetails) {
        if (cartDetails[index].status === 'ordered') {
          cartDetails[index].attributes.fiscal_data = {
            ...cartDetails[index].attributes.fiscal_data,
            write_off_date: writeOffCorrection.write_off_date,
            write_off_invoice: writeOffCorrection.write_off_invoice
          }
        }
      }
      setCartDetails({ ...cartDetails })
      setIsWriteOffModalOpen(false)
      toast.success('Dados da nota fiscal atualizados com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ??
          'Não foi possível atualizar os dados da nota fiscal!'
      )
    }
  }, [cartDetails, orderId, writeOffCorrection])

  const onSaveEnterprise = useCallback(async () => {
    if (selectedEnterpriseId == null) return
    try {
      await assignEnterprise(orderId, itemsSelected, selectedEnterpriseId)
      const enterpriseName = enterprises.find(
        (e) => e.id === selectedEnterpriseId
      )?.name
      for (const imei of Object.keys(cartDetails)) {
        const item = cartDetails[imei]
        if (item.id != null && itemsSelected.includes(item.id as number)) {
          ;(item.attributes as any).enterprise_id = selectedEnterpriseId
          ;(item.attributes as any).enterprise_name = enterpriseName
        }
      }
      setCartDetails({ ...cartDetails })
      setItemsSelected([])
      setIsEnterpriseModalOpen(false)
      toast.success('Empresa atribuída com sucesso!')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível atribuir a empresa!'
      )
    }
  }, [
    assignEnterprise,
    cartDetails,
    itemsSelected,
    orderId,
    selectedEnterpriseId,
    enterprises
  ])

  const onSave = useCallback(async () => {
    try {
      const cartItems = Array.from(cart)
      const cartContainsInvalidItems = cartItems.find(
        (item) => cartDetails[item].id === undefined
      )

      if (cartContainsInvalidItems) {
        toast.error('Revise os rastreadores inválidos e tente novamente!')
      } else {
        const newItems = cartItems.filter((item) => !cartDetails[item].saved)

        await addItems(
          orderId,
          newItems.map((item) => cartDetails[item].id as number)
        )
        toast.success('Rastreadores adicionados com sucesso!')

        const newItemsDetails = newItems.reduce((cart, item) => {
          cart[item] = makeSavedDeviceDetails({
            id: cartDetails[item].id as number,
            ...cartDetails[item].attributes,
            itemStatus: 'ordered'
          } as any)
          return cart
        }, {} as HardwareCart)

        setCartDetails({
          ...cartDetails,
          ...newItemsDetails
        })
      }
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ??
          'Falha ao tentar adicionar os rastreadores ao pedido!'
      )
    }
  }, [cart, cartDetails, orderId])

  const onSaveCancelFiscalData = useCallback(async () => {
    const payload = {
      write_off_date: fiscalData.write_off_date || new Date(),
      write_off_invoice: fiscalData.write_off_invoice,
      write_off_invoice_motive:
        fiscalData.write_off_invoice_motive ||
        writeOffOptions.canceled[0].value ||
        ''
    }

    try {
      await cancel(orderId, payload, itemToBeUpdatedAtRef.current)

      const data = await show(orderId, {
        attributes: ['id', 'current_status']
      })

      updateOrderStatus(data?.current_status as OrderStatus)
      toast.success('Pedido cancelado com sucesso!')

      for (const item of Array.from(cart)) {
        cartDetails[item].attributes.fiscal_data = { ...payload }
        cartDetails[item].status = 'canceled'
      }

      setCartDetails({ ...cartDetails })
    } catch (err: any) {
      toast.error(err.suggestedMessage ?? 'Não foi possível cancelar o pedido!')
    }

    setCurrentModal(null)
  }, [cart, cartDetails, fiscalData, orderId, updateOrderStatus])

  const onChangeItemStatusRequest = useCallback(async (imei?: string) => {
    setFiscalData({
      write_off_date: undefined,
      write_off_invoice: undefined,
      write_off_invoice_motive: undefined
    })

    if (imei) setSelectedDevice(imei)

    setCurrentModal('update_item_status')
  }, [])

  const onSaveChangeItemStatus = useCallback(async () => {
    try {
      const payload = itemsSelected.map((id) => ({
        attributes: {
          write_off_date: fiscalData.write_off_date || new Date(),
          write_off_invoice: fiscalData.write_off_invoice,
          write_off_invoice_motive:
            fiscalData.write_off_invoice_motive ||
            writeOffOptions[currentOrderHardwareStatus][0].value ||
            '',
          item_id: id as number,
          created_at: itemToBeUpdatedAt
        }
      }))

      await updateItemStatus(orderId, currentOrderHardwareStatus, payload)

      toast.success('Estado(s) alterado(s) com sucesso!')

      Object.values(cartDetails).forEach((item) => {
        if (itemsSelected.includes(item.id as number)) {
          item.attributes.fiscal_data = {
            write_off_date: fiscalData.write_off_date || new Date(),
            write_off_invoice: fiscalData.write_off_invoice,
            write_off_invoice_motive:
              fiscalData.write_off_invoice_motive ||
              writeOffOptions[currentOrderHardwareStatus][0].value ||
              ''
          }
          item.status = currentOrderHardwareStatus
          item.status_updated_at = itemToBeUpdatedAt
        }
      })

      setItemsSelected([])

      setCartDetails({ ...cartDetails })
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ??
          'Não foi possível atualizar o(s) estado(s) do(s) rastreador(es)!'
      )
    }

    setCurrentModal(null)
  }, [
    cartDetails,
    currentOrderHardwareStatus,
    fiscalData,
    orderId,
    selectedDevice,
    itemToBeUpdatedAt,
    itemsSelected
  ])

  const cancelOrderModalAttributes = {
    title: 'Cancelar pedido',
    message: 'Tem certeza que deseja cancelar este pedido?',
    onConfirm: onCancelConfirmation,
    onDateChange: onCanceledAtChange
  }

  const getUpdateOrderStatusModalAttributes = (status: string) => ({
    title: 'Atualizar estado',
    message: `Alterar o estado do pedido para: ${translations.order_status[status]}?`,
    onConfirm: onStatusChangeConfirmation,
    onDateChange: onOrderStatusUpdatedAtChange
  })

  return isLoading ? (
    <LoadPage />
  ) : !order ? (
    <NotFoundPage />
  ) : (
    <div className="overflow-hidden px-4">
      <div className="flex flex-row justify-between items-center py-4 m-4 animate-fade-in-down">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Pedido: {order.token}{' '}
          {currentOrderStatus === 'canceled' && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              {`Cancelado em ${date(order.closed_at as Date)}`}
            </span>
          )}
          {currentOrderStatus === 'migrated' && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-sky-50 text-sky-700">
              {`Migrado em ${date(order.closed_at as Date)}`}
            </span>
          )}
        </h3>
      </div>

      <AssignOwnerModal
        open={isAssignOwnerOpen}
        setOpen={setIsAssignOwnerOpen}
        orderId={order.id}
        hasOwner={order.relationships?.staff_member != null}
        onAssigned={(staffMember) =>
          setOrder((prev) =>
            prev == null
              ? prev
              : {
                  ...prev,
                  relationships: {
                    ...prev.relationships,
                    staff_member: staffMember
                  }
                }
          )
        }
      />

      <StepPanel
        steps={steps}
        statusDates={statusDates}
        onStatusChangeRequest={onStatusChangeRequest}
        onDateEditRequest={
          [Role.ROOT, Role.MANAGER, Role.FINANCE].includes(userRole)
            ? onOrderDateEditRequest
            : undefined
        }
        clientStatus={clientStatus}
        planType={product?.type}
      />

      <div className="flex">
        <div className="w-full">
          <ReviewTable
            order={order}
            client={client}
            plan={plan}
            onAssignOwnerRequest={
              [Role.ROOT, Role.MANAGER].includes(userRole)
                ? () => setIsAssignOwnerOpen(true)
                : undefined
            }
          />
        </div>

        <ProtectedSection roles={[Role.MANAGER, Role.FINANCE, Role.SALES]}>
          {installments.length > 0 && (
            <div className="mx-4 my-8 w-1/2">
              <InstallmentTable
                type={'Equipamentos'}
                currency={plan?.currency ?? Currency.brl}
                installments={installments}
                onDateEditRequest={
                  [Role.ROOT, Role.MANAGER, Role.FINANCE].includes(userRole)
                    ? onInstallmentDateEditRequest
                    : undefined
                }
              />
            </div>
          )}
        </ProtectedSection>
      </div>

      {/* Order progress session */}
      <ProtectedSection roles={[Role.MANAGER, Role.LOGISTICS]}>
        {includeStatus(currentOrderStatus, [
          OrderStatus.in_progress,
          OrderStatus.updating_fiscal_data
        ]) && (
          <div className="flex p-6 m-4 rounded shadow animate-fade-in-down">
            {currentOrderStatus === OrderStatus.in_progress && (
              <>
                <div className="flex flex-row w-3/4 items-center space-x-4">
                  <div className="w-1/3">
                    <InputWithValidationError
                      autoFocus
                      label=""
                      type="text"
                      name="imei"
                      id="imei"
                      placeholder="imei"
                      value={currentImei}
                      disabled={disabled || cart.size >= (order?.quantity ?? 0)}
                      onKeyPress={(e: any) =>
                        e.key === 'Enter' ? onAddToCart(currentImei) : null
                      }
                      onChange={(value) => setCurrentImei(value)}
                      error={errors?.imei}
                    />
                  </div>
                  <div className="relative group mt-1">
                    <SimpleButton
                      disabled={cart.size >= (order?.quantity ?? 0)}
                      className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={() => onAddToCart(currentImei)}
                    >
                      Adicionar
                    </SimpleButton>
                    {cart.size >= (order?.quantity ?? 0) && (
                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        O pedido já está cheio
                      </span>
                    )}
                  </div>
                  <div className="relative group mt-1">
                    <SimpleButton
                      disabled={cart.size >= (order?.quantity ?? 0)}
                      className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={() => setIsBulkModalOpen(true)}
                    >
                      Inserção em lote
                    </SimpleButton>
                    {cart.size >= (order?.quantity ?? 0) && (
                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        O pedido já está cheio
                      </span>
                    )}
                  </div>
                  <div className="mt-1">
                    {`Adicionados: ${cart.size} de ${order.quantity}`}
                  </div>
                </div>
                <div className="flex flex-row w-1/4 gap-x-5 justify-end">
                  <button
                    type="button"
                    className=" bg-white py-2 px-8 border rounded-md shadow-sm text-sm font-sm text-emerald-700 hover:bg-gray-50 focus:outline-none"
                    onClick={() => onRefreshComunication()}
                  >
                    <div className="flex flex-row items-center justify-center">
                      <RefreshIcon className="h-4 w-4" aria-hidden="true" />
                      <div className="mx-3">Atualizar</div>
                    </div>
                  </button>
                  {!(cart.size >= (order?.quantity ?? 0)) &&
                    plan?.nature === Nature.service && (
                      <SimpleButton
                        type="button"
                        className="bg-white py-2 px-8 border rounded-md shadow-sm text-sm font-sm text-blue-700 hover:bg-gray-50 focus:outline-none"
                        onClick={() => setIsMigrateModalOpen(true)}
                        disabled={cart.size >= (order?.quantity ?? 0)}
                      >
                        <div className="flex flex-row items-center justify-center">
                          <UploadIcon className="h-4 w-4" aria-hidden="true" />
                          <div className="mx-3">Migrar itens</div>
                        </div>
                      </SimpleButton>
                    )}
                </div>
              </>
            )}
            {includeStatus(currentOrderStatus, [
              OrderStatus.updating_fiscal_data
            ]) &&
              plan?.fiscal_data && (
                <ProtectedSection
                  roles={[Role.MANAGER, Role.FINANCE, Role.LOGISTICS]}
                >
                  <div className="flex flex-row w-full space-x-4">
                    <div className="w-1/5">
                      <InputWithValidationError
                        label="Nota fiscal de saída"
                        type="text"
                        name="write_off_invoice"
                        id="write_off_invoice"
                        value={fiscalData.write_off_invoice ?? ''}
                        onChange={(value) =>
                          setFiscalData({
                            ...fiscalData,
                            write_off_invoice: value
                          })
                        }
                      />
                    </div>
                    <div className="w-1/5">
                      <SimpleCustomSelect
                        label="Motivo"
                        options={
                          plan.nature === 'service'
                            ? writeOffMotives.service
                            : writeOffMotives.good
                        }
                        value={fiscalData.write_off_invoice_motive}
                        onChange={(value) =>
                          setFiscalData({
                            ...fiscalData,
                            write_off_invoice_motive:
                              value as WriteOffInvoiceMotives
                          })
                        }
                      />
                    </div>
                    <div className="w-1/5">
                      <DatePickerWithError
                        label="Data de emissão"
                        name="orderedAt"
                        date={
                          fiscalData.write_off_date
                            ? fiscalData.write_off_date
                            : new Date()
                        }
                        onChange={(value) =>
                          setFiscalData({
                            ...fiscalData,
                            write_off_date: value ? new Date(value) : undefined
                          })
                        }
                      />
                    </div>
                    <div className="mt-6">
                      <SimpleButton onClick={() => onSaveFiscalData()}>
                        Salvar
                      </SimpleButton>
                    </div>
                  </div>
                </ProtectedSection>
              )}
          </div>
        )}
      </ProtectedSection>

      {!isEmpty(cartDetails) &&
        currentOrderStatus === OrderStatus.delivered && (
          <div className="flex items-center mb-2 py-2 bg-gray-50 sm:px-4 lg:px-6 mx-4 shadow border-gray-200 sm:rounded-lg font-medium text-sm">
            <div className="flex justify-center mr-5">
              <PieChart width={28} height={28}>
                <Pie
                  data={[
                    {
                      name: 'Ativos',
                      value: itemsNumberByStatus?.ordered || 0,
                      color: '#10B981'
                    },
                    {
                      name: 'Troca de titularidade',
                      value: itemsNumberByStatus?.ownership_changed || 0,
                      color: '#1D4ED8'
                    },
                    {
                      name: 'Devolvidos',
                      value: itemsNumberByStatus?.returned || 0,
                      color: '#6B7280'
                    },
                    {
                      name: 'Migrados',
                      value: itemsNumberByStatus?.migrated || 0,
                      color: '#0369a1'
                    },
                    {
                      name: 'Cancelados',
                      value: itemsNumberByStatus?.canceled || 0,
                      color: '#DC2626'
                    }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={7}
                  outerRadius={14}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { color: '#10B981' },
                    { color: '#1D4ED8' },
                    { color: '#6B7280' },
                    { color: '#0369a1' },
                    { color: '#DC2626' }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </div>

            <div className="flex items-center w-full gap-x-8 text-center ">
              {(() => {
                const totalItems =
                  (itemsNumberByStatus?.ordered || 0) +
                  (itemsNumberByStatus?.ownership_changed || 0) +
                  (itemsNumberByStatus?.returned || 0) +
                  (itemsNumberByStatus?.canceled || 0) +
                  (itemsNumberByStatus?.migrated || 0)
                return (
                  <>
                    <div className="text-sm">
                      <div className="font-bold">
                        Total: {totalItems} (
                        {Math.round((totalItems / totalItems) * 100)}%)
                      </div>
                    </div>

                    <div className="text-emerald-600">{`Ativos: ${
                      itemsNumberByStatus?.ordered
                    } (${Math.round(
                      ((itemsNumberByStatus?.ordered || 0) / totalItems) * 100
                    )}%)`}</div>
                    <div className="text-blue-800">{`Troca de titularidade: ${
                      itemsNumberByStatus?.ownership_changed
                    } (${Math.round(
                      ((itemsNumberByStatus?.ownership_changed || 0) /
                        totalItems) *
                        100
                    )}%)`}</div>
                    <div className="text-gray-500">{`Devolvidos: ${
                      itemsNumberByStatus?.returned
                    } (${Math.round(
                      ((itemsNumberByStatus?.returned || 0) / totalItems) * 100
                    )}%)`}</div>
                    <div className="text-sky-700">{`Migrados: ${
                      itemsNumberByStatus?.migrated
                    } (${Math.round(
                      ((itemsNumberByStatus?.migrated || 0) / totalItems) * 100
                    )}%)`}</div>
                    <div className="text-red-700">{`Cancelados: ${
                      itemsNumberByStatus?.canceled
                    } (${Math.round(
                      ((itemsNumberByStatus?.canceled || 0) / totalItems) * 100
                    )}%)`}</div>
                  </>
                )
              })()}
            </div>
          </div>
        )}

      <div id="order-items-table">
        {!isEmpty(cartDetails) &&
          !includeStatus(currentOrderStatus, [
            OrderStatus.pending,
            OrderStatus.approved,
            OrderStatus.in_line
          ]) && (
            <div className="flex flex-col px-4 animate-fade-in-down">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
                  <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        {itemsSelected.length > 0 ? (
                          <tr>
                            <th
                              className="h-14"
                              colSpan={
                                includeStatus(currentOrderStatus, [
                                  OrderStatus.ready_for_pick_up,
                                  OrderStatus.shipping,
                                  OrderStatus.delivered
                                ])
                                  ? includeStatus(currentOrderStatus, [
                                      OrderStatus.ready_for_pick_up,
                                      OrderStatus.shipping
                                    ])
                                    ? 10
                                    : 9
                                  : 8
                              }
                            >
                              <div className="flex items-center space-x-4 pl-3">
                                <div className="ml-2">
                                  <CheckBox
                                    disabled={Object.values(cartDetails).every(
                                      (item) => item.status !== 'ordered'
                                    )}
                                    name="selectAllItems"
                                    label=""
                                    checked={Object.values(cartDetails)
                                      .filter(
                                        (item) => item.status === 'ordered'
                                      )
                                      .every((item) =>
                                        itemsSelected.includes(
                                          item.id as number
                                        )
                                      )}
                                    onChange={handleAllCheckBoxChange}
                                  />
                                </div>
                                <ProtectedSection
                                  roles={[
                                    Role.MANAGER,
                                    Role.FINANCE,
                                    Role.LOGISTICS
                                  ]}
                                >
                                  <div className="flex gap-x-3 w-full font-normal text-md pr-3">
                                    {includeStatus(currentOrderStatus, [
                                      OrderStatus.delivered
                                    ]) && (
                                      <SimpleButton
                                        className="relative inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-emerald-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-emerald-500 h-18 w-1/2 flex justify-center"
                                        onClick={() =>
                                          onChangeItemStatusRequest()
                                        }
                                      >
                                        <p>
                                          Escolher novo estado para os items
                                          selecionados
                                        </p>
                                        <PencilIcon
                                          className="ml-3 h-4 w-4"
                                          aria-hidden="true"
                                        />
                                      </SimpleButton>
                                    )}
                                    {includeStatus(currentOrderStatus, [
                                      OrderStatus.ready_for_pick_up,
                                      OrderStatus.shipping
                                    ]) && (
                                      <div
                                        className={`relative group ${
                                          includeStatus(currentOrderStatus, [
                                            OrderStatus.delivered
                                          ])
                                            ? 'w-1/2'
                                            : 'w-full'
                                        }`}
                                      >
                                        <SimpleButton
                                          disabled={enterprises.length === 0}
                                          className="relative inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-purple-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-purple-500 h-18 flex justify-center disabled:opacity-50 disabled:cursor-not-allowed w-full"
                                          onClick={() =>
                                            setIsEnterpriseModalOpen(true)
                                          }
                                        >
                                          <p>Atribuir a empresa</p>
                                          <PencilIcon
                                            className="ml-3 h-4 w-4"
                                            aria-hidden="true"
                                          />
                                        </SimpleButton>
                                        {enterprises.length === 0 && (
                                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 pointer-events-none">
                                            Este cliente não possui subempresas
                                            cadastradas
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <SimpleButton
                                      className={`relative inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-blue-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-blue-500 h-18 flex justify-center ${
                                        includeStatus(currentOrderStatus, [
                                          OrderStatus.delivered
                                        ])
                                          ? 'w-1/2'
                                          : 'w-full'
                                      }`}
                                      onClick={() =>
                                        setIsWriteOffModalOpen(true)
                                      }
                                    >
                                      <p>Editar nota fiscal</p>
                                      <PencilIcon
                                        className="ml-3 h-4 w-4"
                                        aria-hidden="true"
                                      />
                                    </SimpleButton>
                                  </div>
                                </ProtectedSection>
                              </div>
                            </th>
                          </tr>
                        ) : (
                          <tr>
                            {includeStatus(currentOrderStatus, [
                              OrderStatus.ready_for_pick_up,
                              OrderStatus.shipping,
                              OrderStatus.delivered
                            ]) && (
                              <ProtectedSection
                                roles={[
                                  Role.MANAGER,
                                  Role.FINANCE,
                                  Role.LOGISTICS
                                ]}
                              >
                                <th
                                  scope="col"
                                  className="pb-3 pr-3 pt-3 text-left text-xs font-medium text-gray-500 uppercase"
                                >
                                  <div className="ml-5">
                                    <CheckBox
                                      disabled={Object.values(
                                        cartDetails
                                      ).every(
                                        (item) => item.status !== 'ordered'
                                      )}
                                      name="selectAllItems"
                                      label=""
                                      checked={Object.values(cartDetails)
                                        .filter(
                                          (item) => item.status === 'ordered'
                                        )
                                        .every((item) =>
                                          itemsSelected.includes(
                                            item.id as number
                                          )
                                        )}
                                      onChange={handleAllCheckBoxChange}
                                    />
                                  </div>
                                </th>
                              </ProtectedSection>
                            )}
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {includeStatus(currentOrderStatus, [
                                OrderStatus.in_line,
                                OrderStatus.in_progress,
                                OrderStatus.updating_fiscal_data
                              ])
                                ? 'Válido'
                                : 'Status'}
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Imei / Identificador
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Marca / Modelo
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Última comunicação / GPS
                            </th>
                            {includeStatus(currentOrderStatus, [
                              OrderStatus.in_progress
                            ]) && (
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                GPS válido
                              </th>
                            )}
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Serial / Número
                            </th>
                            {includeStatus(currentOrderStatus, [
                              OrderStatus.ready_for_pick_up,
                              OrderStatus.shipping,
                              OrderStatus.delivered
                            ]) && (
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                Empresa
                              </th>
                            )}
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Nota fiscal de saída
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              Motivo da saída
                            </th>
                            {!includeStatus(currentOrderStatus, [
                              OrderStatus.delivered
                            ]) && (
                              <th
                                scope="col"
                                className="p-3 text-left text-xs font-medium text-gray-500 uppercase"
                              >
                                Ações
                              </th>
                            )}
                          </tr>
                        )}
                      </thead>
                      <tbody className="text-left bg-white divide-y divide-gray-200">
                        {Array.from(cart)
                          .reverse()
                          .map((imei, index) => (
                            <tr
                              key={index}
                              className={index % 2 ? 'bg-gray-50' : 'bg-white'}
                            >
                              {includeStatus(currentOrderStatus, [
                                OrderStatus.ready_for_pick_up,
                                OrderStatus.shipping,
                                OrderStatus.delivered
                              ]) && (
                                <ProtectedSection
                                  roles={[
                                    Role.MANAGER,
                                    Role.FINANCE,
                                    Role.LOGISTICS
                                  ]}
                                >
                                  <td>
                                    <div className="ml-5">
                                      <CheckBox
                                        disabled={
                                          cartDetails[imei].status !== 'ordered'
                                        }
                                        name="selectItem"
                                        label=""
                                        checked={itemsSelected.includes(
                                          cartDetails[imei].id! as number
                                        )}
                                        onChange={() =>
                                          handleCheckBoxChange(
                                            cartDetails[imei].id! as number
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                </ProtectedSection>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    {includeStatus(currentOrderStatus, [
                                      OrderStatus.in_line,
                                      OrderStatus.in_progress,
                                      OrderStatus.updating_fiscal_data
                                    ]) ? (
                                      cartDetails[imei].hint()
                                    ) : (
                                      <div className="flex flex-col items-center justify-center">
                                        <Label
                                          type="item_status"
                                          label={
                                            translations['item_status'][
                                              cartDetails[imei].status as string
                                            ]
                                          }
                                          value={cartDetails[imei].status}
                                        />
                                        <div className="ml-2 text-xs text-left text-gray-500">
                                          {date(
                                            cartDetails[imei].status_updated_at
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.imei ||
                                    imei ||
                                    '-'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {cartDetails[imei].attributes.name || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.relationships
                                    ?.type?.attributes?.brand_alias || '-'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {cartDetails[imei].attributes.relationships
                                    ?.type?.attributes?.alias || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.relationships
                                    ?.last_gps?.attributes
                                    ?.last_connection_time || '-'}
                                </div>
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.relationships
                                    ?.last_gps?.attributes?.last_gps_time ||
                                    '-'}
                                </div>
                              </td>
                              {includeStatus(currentOrderStatus, [
                                OrderStatus.in_progress
                              ]) && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {cartDetails[imei].attributes.relationships
                                    ?.last_gps?.attributes?.last_gps_raw
                                    ? (() => {
                                        const lastConnection =
                                          cartDetails[imei].attributes
                                            .relationships?.last_gps.attributes
                                            .last_gps_raw
                                        const { lat, lng } = lastConnection

                                        return isInSoftruckBBOX(lat, lng) ? (
                                          <CheckCircleIcon
                                            className="h-4 w-4 text-green-500"
                                            aria-hidden="true"
                                          />
                                        ) : (
                                          <BanIcon
                                            className="h-4 w-4 text-red-500"
                                            aria-hidden="true"
                                          />
                                        )
                                      })()
                                    : '-'}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.relationships
                                    ?.chips?.attributes?.serial || '-'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {cartDetails[imei].attributes.relationships
                                    ?.chips?.attributes?.number || '-'}
                                </div>
                              </td>
                              {includeStatus(currentOrderStatus, [
                                OrderStatus.ready_for_pick_up,
                                OrderStatus.shipping,
                                OrderStatus.delivered
                              ]) && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {(cartDetails[imei].attributes as any)
                                    .enterprise_name ||
                                    (cartDetails[imei].attributes as any)
                                      .relationships?.enterprise?.attributes
                                      ?.name ||
                                    '-'}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="text-sm text-gray-900">
                                  {cartDetails[imei].attributes.fiscal_data
                                    ?.write_off_invoice || '-'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {cartDetails[imei].attributes.fiscal_data
                                    ?.write_off_date
                                    ? date(
                                        cartDetails[imei].attributes.fiscal_data
                                          ?.write_off_date
                                      )
                                    : '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {translations.write_off_motives[
                                  cartDetails[imei].attributes.fiscal_data
                                    ?.write_off_invoice_motive as WriteOffInvoiceMotives
                                ] || '-'}
                              </td>
                              {!includeStatus(currentOrderStatus, [
                                OrderStatus.delivered
                              ]) && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {includeStatus(currentOrderStatus, [
                                    OrderStatus.in_progress
                                  ]) &&
                                    !cartDetails[imei].saved && (
                                      <button
                                        type="button"
                                        className="mr-2 bg-white py-2 px-4 border rounded-md shadow-sm text-sm font-sm text-emerald-700 hover:bg-gray-50 focus:outline-none"
                                        onClick={() => validateDevice(imei)}
                                      >
                                        <RefreshIcon
                                          className="h-4 w-4"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    )}
                                  {includeStatus(currentOrderStatus, [
                                    OrderStatus.in_progress
                                  ]) ? (
                                    cartDetails[imei].saved ? (
                                      <button
                                        type="button"
                                        className="bg-white py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-red-700 hover:bg-gray-50 focus:outline-none"
                                        onClick={() =>
                                          onRemoveFromOrderRequest(imei)
                                        }
                                      >
                                        <TrashIcon
                                          className="h-4 w-4"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="bg-white py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-red-700 hover:bg-gray-50 focus:outline-none"
                                        onClick={() => onRemoveFrontCart(imei)}
                                      >
                                        <XIcon
                                          className="h-4 w-4"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    )
                                  ) : null}
                                </td>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

        <ProtectedSection roles={[Role.MANAGER, Role.LOGISTICS]}>
          {currentOrderStatus === OrderStatus.in_progress && (
            <div className="m-4 flex justify-end animate-fade-in-down">
              <button
                type="button"
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                onClick={() => onSave()}
              >
                Salvar
              </button>
            </div>
          )}
        </ProtectedSection>
      </div>

      {/* Modal de correção de nota fiscal pós-expedição */}
      <Modal
        open={isWriteOffModalOpen}
        title="Editar nota fiscal"
        onClose={() => setIsWriteOffModalOpen(false)}
      >
        <div className="flex flex-col gap-y-4 mt-4">
          <p className="text-sm text-gray-500">
            Todos os itens com status <strong>Pedido</strong> deste pedido serão
            atualizados com as informações abaixo.
          </p>
          <InputWithValidationError
            label="Nota fiscal de saída"
            type="text"
            name="write_off_invoice_correction"
            id="write_off_invoice_correction"
            value={writeOffCorrection.write_off_invoice ?? ''}
            onChange={(value) =>
              setWriteOffCorrection({
                ...writeOffCorrection,
                write_off_invoice: value
              })
            }
          />
          <DatePickerWithError
            label="Data de emissão"
            name="write_off_date_correction"
            date={writeOffCorrection.write_off_date}
            onChange={(value) =>
              setWriteOffCorrection({
                ...writeOffCorrection,
                write_off_date: value ? new Date(value) : new Date()
              })
            }
          />
          <div className="flex justify-end mt-2">
            <SimpleButton
              disabled={!writeOffCorrection.write_off_invoice?.trim()}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => onSaveWriteOffCorrection()}
            >
              Salvar
            </SimpleButton>
          </div>
        </div>
      </Modal>

      {/* Modal para atribuição de subempresa */}
      <Modal
        open={isEnterpriseModalOpen}
        title="Atribuir a empresa"
        onClose={() => setIsEnterpriseModalOpen(false)}
      >
        <div className="flex flex-col gap-y-4">
          <p className="text-sm text-gray-600">
            Selecione a empresa para atribuir os {itemsSelected.length} item(s)
            selecionado(s):
          </p>
          <SimpleCustomSelect
            options={[
              { value: undefined, label: 'Selecione uma empresa' },
              ...enterprises.map((e) => ({ value: e.id, label: e.name }))
            ]}
            value={selectedEnterpriseId}
            onChange={(value) =>
              setSelectedEnterpriseId(value as number | undefined)
            }
          />
          <div className="flex justify-end">
            <SimpleButton
              disabled={selectedEnterpriseId == null}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onSaveEnterprise}
            >
              Confirmar
            </SimpleButton>
          </div>
        </div>
      </Modal>

      {/* Modal dinâmico para cancelamento e mudança de estado */}
      <ProtectedSection roles={[Role.MANAGER, Role.FINANCE, Role.LOGISTICS]}>
        {plan?.fiscal_data && currentModal && (
          <HardwareDynamicModal
            isOpen={!!currentModal}
            modalType={currentModal}
            onClose={() => setCurrentModal(null)}
            fiscalData={fiscalData}
            setFiscalData={setFiscalData}
            itemToBeUpdatedAt={itemToBeUpdatedAt}
            setItemToBeUpdatedAt={setItemToBeUpdatedAt}
            currentOrderHardwareStatus={currentOrderHardwareStatus}
            setCurrentOrderItemStatus={setCurrentOrderItemStatus}
            orderActivatedAt={new Date(order.activated_at as Date)}
            onSave={() => {
              if (
                !fiscalData.write_off_invoice ||
                fiscalData.write_off_invoice.trim() === ''
              )
                return
              currentModal === 'cancel_fiscal_data'
                ? onSaveCancelFiscalData()
                : onSaveChangeItemStatus()
            }}
          />
        )}
      </ProtectedSection>

      {![
        OrderStatus.canceled,
        OrderStatus.migrated,
        OrderStatus.delivered
      ].includes(currentOrderStatus) && (
        <div className="py-6 gap-x-2 flex flex-row justify-between animate-fade-in-down">
          <ProtectedSection roles={[Role.MANAGER, Role.FINANCE, Role.SALES]}>
            <div className="ml-3 text-left text-md">
              <label className="font-medium text-gray-700">
                Cancelar pedido
              </label>
              <p className="p-4 text-gray-500">
                Atenção: ao cancelar o pedido todos os rastreadores salvos serão
                deletados.
              </p>

              <div className="">
                <CancelButton
                  label="Cancelar"
                  onClick={onCancelRequest}
                ></CancelButton>
              </div>
            </div>
          </ProtectedSection>
        </div>
      )}

      {/* Modal para migrar itens de outros pedidos */}
      <MigrateOrderItemsModal
        isOpen={isMigrateModalOpen}
        onClose={() => setIsMigrateModalOpen(false)}
        targetOrder={order}
      />

      {/* Modal de inserção em lote */}
      <Modal
        open={isBulkModalOpen}
        title="Inserção em lote de rastreadores"
        onClose={() => {
          setIsBulkModalOpen(false)
          setBulkInput('')
        }}
      >
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Cole os IMEIs abaixo ou importe um CSV.
            </p>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium mr-2"
              onClick={() => csvInputRef.current?.click()}
            >
              Importar CSV
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={onCsvImport}
            />
          </div>
          <ResizibleTextArea
            label=""
            name="bulk_imeis"
            value={bulkInput}
            onChange={setBulkInput}
            placeholder={'123456789012345\n987654321098765'}
          />
          {bulkInput.trim() && (
            <p className="text-s text-gray-500 text-right">
              {bulkInput.split(/[\n,;\s]+/).filter(Boolean).length}/
              {order?.quantity ?? '?'} IMEIs detectados
            </p>
          )}
          <div className="flex justify-end gap-x-3 mt-4">
            <SimpleButton
              onClick={() => {
                setIsBulkModalOpen(false)
                setBulkInput('')
              }}
            >
              Cancelar
            </SimpleButton>
            <SimpleButton
              disabled={!bulkInput.trim()}
              onClick={onBulkAddToCart}
            >
              Processar
            </SimpleButton>
          </div>
        </div>
      </Modal>
    </div>
  )
}
